import { styled } from '@mui/material';
import { PageContext } from 'pages/Login/PageProvider';
import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, Form, Link, PasswordInput, Stack, Text, TextInput, ThirdPartyButton } from 'shell/components';

const LoginInputs = ({ device, errors, form, handleChange, handleClickButton, signInWithGoogle }) => {
    const { loading, emailSubmitted, setEmailSubmitted } = useContext(PageContext);
    const { t } = useTranslation('auth');

    return (
        <LoginBox device={device}>
            <Stack sx={{ mb: 4 }} alignItems={'center'}>
                <Text component={'h1'} variant={'h6'} weight={'medium'}>
                    {emailSubmitted
                        ? t('auth:login.enter-password', 'Enter your password')
                        : t('auth:login.title', 'Log in to your account')}
                </Text>
            </Stack>
            <Form
                onSubmit={() => {
                    handleClickButton('login');
                }}
            >
                <InputStack spacing={6}>
                    {!emailSubmitted ? (
                        <TextInput
                            id={'email-input'}
                            autoComplete={'username'}
                            autoFocus
                            error={Boolean(errors.email)}
                            helperText={errors.email}
                            value={form.email}
                            label={t('auth:form.email-label', 'Email')}
                            onChange={(e) => handleChange('email', e.target.value)}
                            placeholder={t('auth:form.email-placeholder', 'Enter email')}
                            required
                        />
                    ) : (
                        <>
                            <Stack spacing={1}>
                                <Text variant={'p1'}>{t('auth:login.log-in-for', 'Log in for')}</Text>
                                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        {form.email}
                                    </Text>
                                    <Button
                                        variant={'borderless'}
                                        size={'small'}
                                        onClick={() => setEmailSubmitted(false)}
                                    >
                                        {t('common:controls.change', 'Change')}
                                    </Button>
                                </Stack>
                            </Stack>
                            <PasswordInput
                                id={'password-input'}
                                autoComplete={'current-password'}
                                autoFocus
                                error={Boolean(errors.password)}
                                helperText={errors.password}
                                value={form.password}
                                label={t('auth:form.password-label', 'Password')}
                                onChange={(e) => handleChange('password', e.target.value)}
                                placeholder={t('auth:form.password-placeholder', 'Enter password')}
                                required
                            />
                        </>
                    )}
                </InputStack>
                {emailSubmitted && (
                    <Stack sx={{ marginBottom: 6, width: 'fit-content' }}>
                        <Text>
                            <Link
                                data-cy={'forgot-password-link'}
                                to={{ pathname: '/forgot-password', state: { email: form.email } }}
                            >
                                {t('auth:login.forgot_password', 'Forgot your password?')}
                            </Link>
                        </Text>
                    </Stack>
                )}
                <Stack sx={{ marginBottom: 3 }}>
                    <Button data-cy={'login-button'} loading={loading} type={'submit'}>
                        {t('auth:login.login', 'Login')}
                    </Button>
                </Stack>
                <Stack alignItems={'center'}>
                    <SignUpText variant={'p3'}>
                        <Trans
                            i18nKey='auth:login.dont_have_an_account'
                            components={{
                                'sign-up': <Link to={'/sign-up'} />,
                            }}
                        />
                    </SignUpText>
                </Stack>
                <DividerStack alignItems={'center'} direction={'row'}>
                    <HalfDivider />
                    <Text sx={{ margin: '0 10px' }} color={'light'}>
                        {t('auth:login.or', 'OR')}
                    </Text>
                    <HalfDivider />
                </DividerStack>
                <Stack>
                    <ThirdPartyButton variant={'google'} onClick={signInWithGoogle} />
                </Stack>
            </Form>
        </LoginBox>
    );
};

const LoginBox = styled(Box)(({ device, theme }) => {
    return {
        marginTop: theme.spacing(12),
        padding: device === 'mobile' ? theme.spacing(7, 4) : theme.spacing(7),
        width: device === 'mobile' ? '100%' : 448,
        height: 'fit-content',
        background: device === 'mobile' ? 'transparent' : theme.palette.ensightful.shades[0],
        boxShadow: device === 'mobile' ? 'none' : theme.palette.ensightful.shadows[1],
        borderRadius: '20px',
    };
});

const InputStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0, 2),
    };
});

const SignUpText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.neutral[700],
    };
});

const DividerStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0),
    };
});

const HalfDivider = styled(Box)(({ theme }) => {
    return {
        flex: 1,
        height: '1px',
        backgroundColor: '#E0E0E0',
    };
});

export default LoginInputs;
